import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type {
  CreateTagInput,
  Tag,
  TagListQuery,
  TagListResponse,
  UpdateTagInput,
} from '@features/tags/types';

/**
 * Backend /api/v1/taxonomy/tags DTO.
 *
 * BE differences vs. FE Tag:
 *   - adds `displayOrder`, `status` (1 = Active).
 *
 * REQ-33 (Faz-7): `usageCount` is now surfaced by the BE (query-time COUNT over
 * the content↔tag relation, deleted content excluded) — mapped through below.
 */
interface TagBeDto {
  id: string;
  name: string | null;
  slug: string | null;
  description: string | null;
  color: string | null;
  displayOrder: number;
  status: number;
  usageCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface CreateTagBeRequest {
  name: string;
  slug?: string | null;
  description?: string | null;
  color?: string | null;
  displayOrder?: number;
}

type UpdateTagBeRequest = CreateTagBeRequest;

const BASE = '/api/v1/taxonomy/tags';

function mapTag(b: TagBeDto): Tag {
  return {
    id: b.id,
    name: b.name ?? '',
    slug: b.slug ?? '',
    color: b.color ?? '#2563EB',
    description: b.description ?? undefined,
    usageCount: b.usageCount ?? 0,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

export const tagsApi = {
  async list(query: TagListQuery = {}): Promise<TagListResponse> {
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.q) params.search = query.q;
    if (query.sort === 'name') {
      params.sortBy = 'name';
      params.descending = false;
    } else if (query.sort === 'recent') {
      params.sortBy = 'updatedAt';
      params.descending = true;
    } else if (query.sort === 'usage') {
      // REQ-33 (Faz-7): BE now sorts by the derived usage count.
      params.sortBy = 'usageCount';
      params.descending = true;
    }
    const res = await http.get<ActionResponse<TagBeDto>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapTag),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? (query.page ?? 1),
      pageSize: pagination?.pageSize ?? (query.pageSize ?? 20),
      totalPages: pagination?.totalPages ?? 1,
    };
  },

  async get(id: string): Promise<Tag> {
    const dto = unwrap(await http.get<ActionResponse<TagBeDto>>(`${BASE}/${id}`));
    return mapTag(dto);
  },

  async create(input: CreateTagInput): Promise<Tag> {
    const body: CreateTagBeRequest = {
      name: input.name,
      slug: input.slug,
      description: input.description ?? null,
      color: input.color,
    };
    const id = unwrap(await http.post<ActionResponse<string>>(BASE, body));
    const dto = unwrap(await http.get<ActionResponse<TagBeDto>>(`${BASE}/${id}`));
    return mapTag(dto);
  },

  async update(id: string, input: UpdateTagInput): Promise<Tag> {
    const current = await this.get(id);
    const body: UpdateTagBeRequest = {
      name: input.name ?? current.name,
      slug: input.slug ?? current.slug,
      description: input.description ?? current.description ?? null,
      color: input.color ?? current.color,
    };
    await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body);
    const dto = unwrap(await http.get<ActionResponse<TagBeDto>>(`${BASE}/${id}`));
    return mapTag(dto);
  },

  async remove(id: string): Promise<void> {
    await http.delete<ActionResponse<unknown>>(`${BASE}/${id}`);
  },

  async bulkDelete(ids: string[]): Promise<{ deleted: number; skipped: number; skippedIds: string[] }> {
    const dto = unwrap(
      await http.post<
        ActionResponse<{ deleted: number; skipped: number; skippedIds: string[] | null }>
      >(`${BASE}/bulk-delete`, { ids }),
    );
    return { deleted: dto.deleted, skipped: dto.skipped, skippedIds: dto.skippedIds ?? [] };
  },
};
